import style from './Icon.module.css';

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * Преобразует цвет (HEX или именованный) в CSS фильтр для перекрашивания изображения
 * @param {string} color - Цвет в формате HEX (#RRGGBB) или имя цвета CSS
 * @returns {string} CSS фильтр или пустая строка, если преобразование невозможно
 */
const colorToFilter = (color) => {
  if (!color) return '';

  // Создаем временный div для получения RGB значений через getComputedStyle
  const tempDiv = document.createElement('div');
  tempDiv.style.color = color;
  document.body.appendChild(tempDiv);

  // Получаем RGB значения
  const computedColor = window.getComputedStyle(tempDiv).color;
  document.body.removeChild(tempDiv);

  // Если цвет не распознан, возвращаем пустую строку
  if (
    !computedColor ||
    (computedColor === 'rgb(0, 0, 0)' && color !== 'black' && color !== '#000000')
  ) {
    return '';
  }

  // Извлекаем RGB компоненты
  const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!rgbMatch) return '';

  const r = parseInt(rgbMatch[1]) / 255;
  const g = parseInt(rgbMatch[2]) / 255;
  const b = parseInt(rgbMatch[3]) / 255;

  // Упрощенный подход к генерации фильтра, для точного преобразования лучше использовать
  // специализированную библиотеку, например, hex-to-css-filter
  return `
    brightness(0) saturate(100%)
    invert(${Math.round(r * 100)}%)
    sepia(${Math.round(g * 50)}%)
    saturate(${Math.round(b * 100 + 1000)}%)
    hue-rotate(${Math.round((Math.atan2(b - r, g - r) * 180) / Math.PI)}deg)
  `;
};

/**
 * Компонент для отображения иконок с возможностью настройки цвета, размеров и других параметров
 *
 * @param {Object} props - Свойства компонента
 * @param {string|Object} props.src - URL, путь к изображению иконки или импортированный SVG (обязательный параметр)
 * @param {string} [props.className=''] - Дополнительные CSS классы
 * @param {string} [props.color] - Цвет иконки (HEX формат или именованный цвет CSS, например, 'orange', 'blue')
 * @param {string} [props.alt='image'] - Альтернативный текст для изображения
 * @param {Function} [props.onClick] - Обработчик клика по иконке
 * @param {boolean} [props.disabled=false] - Флаг отключения иконки
 * @param {string|number} [props.width] - Ширина иконки (CSS значение)
 * @param {string|number} [props.height] - Высота иконки (CSS значение)
 * @param {Object} [props.style] - Дополнительные inline стили
 * @returns {React.ReactElement} Компонент иконки
 *
 * @example
 * // Простое использование с URL
 * <Icon src="/path/to/icon.svg" />
 *
 * @example
 * // С импортированным SVG
 * import logoSvg from '../../assets/svg/logo.svg';
 *
 * <Icon src={logoSvg} color="orange" />
 *
 * @example
 * // С именованным цветом CSS
 * <Icon
 *   src="/path/to/icon.svg"
 *   color="orange"
 * />
 *
 * @example
 * // С HEX цветом
 * <Icon
 *   src="/path/to/icon.svg"
 *   color="#3498db"
 * />
 *
 * @example
 * // В списке с другими элементами
 * <div className="item-list">
 *   {items.map((item) => (
 *     <div key={item.id} className="item">
 *       {item.icon && <Icon src={address_server_short + PUBLIC_ASSETS + item.icon} />}
 *       <span>{item.name}</span>
 *     </div>
 *   ))}
 * </div>
 *
 * @example
 * // С дополнительными стилями и обработчиком события
 * <Icon
 *   src="/path/to/icon.svg"
 *   className="custom-icon"
 *   style={{ marginRight: '8px' }}
 *   color="teal"
 *   onClick={() => handleIconClick()}
 * />
 */
const Icon = ({
  src,
  className = '',
  color,
  alt = 'image',
  onClick,
  disabled = false,
  width,
  height,
  style: customStyle,
  ...restProps
}) => {
  if (!src) {
    console.warn('Icon: свойство src является обязательным');
    return null;
  }

  // Фильтр для изменения цвета иконки
  const applyColorWithFilter = useMemo(() => {
    if (color) {
      try {
        return { filter: colorToFilter(color) };
      } catch (e) {
        console.warn(`Ошибка преобразования цвета '${color}' в фильтр:`, e);
        return {};
      }
    }
    return {};
  }, [color]);

  const combinedStyle = {
    ...(width && { width }),
    ...(height && { height }),
    ...applyColorWithFilter,
    ...customStyle,
  };

  // Обработка случая, когда src - это импортированный SVG или другой файл, а не строка URL
  const imageSrc = typeof src === 'string' ? src : src?.default || src;

  return (
    <img
      src={imageSrc}
      onClick={disabled ? undefined : onClick}
      className={classNames(style.icon, className, {
        [style.disabled]: disabled,
      })}
      alt={alt}
      style={combinedStyle}
      {...restProps}
    />
  );
};

Icon.propTypes = {
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  className: PropTypes.string,
  color: PropTypes.string,
  alt: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
};

export default React.memo(Icon);
